import {
  AppLocale,
  CallerLanguage,
  CallRecordingsResponse,
  CallSession,
  CreateCallSessionRequest,
  PurchaseFulfillResponse,
  RealtimeEvent,
  SignupResponse,
  TopUpProductId,
  TranscriptSegment,
  UserProfile,
  Wallet,
  callRecordingsResponseSchema,
  callSessionSchema,
  purchaseFulfillResponseSchema,
  signupResponseSchema,
  transcriptSegmentSchema,
  walletSchema,
} from "@nativecall/shared";

export const RECORDING_ENABLED =
  (process.env.EXPO_PUBLIC_RECORDING_ENABLED ?? "").toLowerCase() === "true";

const hostedDefaultBaseUrl = "https://nativecall-worker.fhtjayson2025.workers.dev";

function getBaseUrl() {
  if (process.env.EXPO_PUBLIC_API_BASE_URL) {
    return process.env.EXPO_PUBLIC_API_BASE_URL;
  }
  return hostedDefaultBaseUrl;
}

async function request<T>(
  path: string,
  init: RequestInit,
  parser: { parse: (value: unknown) => T } | null = null,
) {
  const response = await fetch(`${getBaseUrl()}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });

  const json = await response.json();

  if (!response.ok) {
    throw new Error(json.error ?? "Request failed.");
  }

  return parser ? parser.parse(json) : (json as T);
}

export async function signUp(input: {
  name: string;
  appLocale: AppLocale;
  defaultCallerLanguage: CallerLanguage;
  acceptedLegal: true;
}) {
  return request<SignupResponse>(
    "/auth/signup",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
    signupResponseSchema,
  );
}

export async function signInWithApple(input: {
  appleUserId: string;
  fullName?: string;
  appLocale: AppLocale;
  defaultCallerLanguage: CallerLanguage;
  acceptedLegal: true;
}) {
  return request<SignupResponse>(
    "/auth/apple",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
    signupResponseSchema,
  );
}

export async function topUpWallet(userId: string, amountCredits: number) {
  return request<Wallet>(
    "/wallet/top-up",
    {
      method: "POST",
      body: JSON.stringify({ userId, amountCredits }),
    },
    walletSchema,
  );
}

export async function fulfillPurchase(input: {
  userId: string;
  productId: TopUpProductId;
  transactionId: string;
}) {
  return request<PurchaseFulfillResponse>(
    "/purchases/fulfill",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
    purchaseFulfillResponseSchema,
  );
}

export async function fetchCallSessions(userId: string) {
  const sessions = await request<CallSession[]>(
    `/call-sessions?userId=${encodeURIComponent(userId)}`,
    {
      method: "GET",
    },
    null,
  );

  return sessions.map((session) => callSessionSchema.parse(session));
}

export async function fetchCallSession(callSessionId: string) {
  return request<CallSession>(
    `/call-sessions/${encodeURIComponent(callSessionId)}`,
    {
      method: "GET",
    },
    callSessionSchema,
  );
}

export async function createCallSession(
  input: CreateCallSessionRequest,
) {
  return request<CallSession>(
    "/call-sessions",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
    callSessionSchema,
  );
}

export async function startCallSession(
  userId: string,
  callSessionId: string,
  options?: {
    stack?: "realtime" | "split";
    record?: boolean;
    mock?: boolean;
  },
) {
  return request<CallSession>(
    `/call-sessions/${callSessionId}/start`,
    {
      method: "POST",
      body: JSON.stringify({
        userId,
        confirmedConsent: true,
        confirmedAcceptableUse: true,
        stack: options?.stack,
        record: options?.record,
        mock: options?.mock,
      }),
    },
    callSessionSchema,
  );
}

export async function endCallSession(userId: string, callSessionId: string) {
  return request<CallSession>(
    `/call-sessions/${callSessionId}/end`,
    {
      method: "POST",
      body: JSON.stringify({ userId }),
    },
    callSessionSchema,
  );
}

export async function cancelCallSession(
  userId: string,
  callSessionId: string,
  reason?: string,
) {
  return request<CallSession>(
    `/call-sessions/${callSessionId}/cancel`,
    {
      method: "POST",
      body: JSON.stringify({ userId, reason }),
    },
    callSessionSchema,
  );
}

export async function deleteAccount(userId: string) {
  return request<{ ok: true }>(
    "/account/delete",
    {
      method: "POST",
      body: JSON.stringify({ userId }),
    },
    null,
  );
}

export async function fetchCallRecordings(
  userId: string,
  callSessionId: string,
): Promise<CallRecordingsResponse> {
  return request<CallRecordingsResponse>(
    `/call-sessions/${encodeURIComponent(callSessionId)}/recordings?userId=${encodeURIComponent(userId)}`,
    {
      method: "GET",
    },
    callRecordingsResponseSchema,
  );
}

export async function fetchTranscript(callSessionId: string) {
  const transcript = await request<TranscriptSegment[]>(
    `/call-sessions/${callSessionId}/transcript`,
    {
      method: "GET",
    },
    null,
  );

  return transcript.map((segment) => transcriptSegmentSchema.parse(segment));
}

export async function createRealtimeClientSecret(userId: string) {
  return request<{ client_secret: { value: string; expires_at: number } }>(
    "/openai/realtime/client-secret",
    {
      method: "POST",
      body: JSON.stringify({ userId }),
    },
    null,
  );
}

export async function createTwilioVoiceToken(userId: string, identity: string) {
  return request<{ token: string; identity: string }>(
    "/twilio/client-token",
    {
      method: "POST",
      body: JSON.stringify({ userId, identity }),
    },
    null,
  );
}

export function connectRealtime(
  user: UserProfile,
  handlers: {
    onOpen?: () => void;
    onEvent: (event: RealtimeEvent) => void;
    onError: () => void;
  },
): { close: () => void } {
  const socketUrl = getBaseUrl()
    .replace("http://", "ws://")
    .replace("https://", "wss://");
  const url = `${socketUrl}/ws?userId=${encodeURIComponent(user.id)}`;

  let socket: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;
  let closedByCaller = false;

  const open = () => {
    if (closedByCaller) return;
    attempt += 1;
    console.log("[realtime] opening", { url, attempt });
    try {
      socket = new WebSocket(url);
    } catch (err) {
      console.log("[realtime] construct_failed", String(err));
      scheduleReconnect();
      return;
    }

    socket.onopen = () => {
      console.log("[realtime] open_ok", { attempt });
      attempt = 0;
      handlers.onOpen?.();
    };

    socket.onmessage = (message) => {
      try {
        const event = JSON.parse(message.data) as RealtimeEvent | { type: string };
        if (event.type !== "system.connected") {
          handlers.onEvent(event as RealtimeEvent);
        }
      } catch (err) {
        console.log("[realtime] parse_failed", String(err));
        handlers.onError();
      }
    };

    socket.onerror = (evt) => {
      // RN passes a generic Event; log best-effort detail
      const anyEvt = evt as unknown as { message?: string; type?: string };
      console.log("[realtime] error", { msg: anyEvt?.message, type: anyEvt?.type });
      handlers.onError();
    };

    socket.onclose = (evt) => {
      console.log("[realtime] close", {
        code: evt.code,
        reason: evt.reason,
        byCaller: closedByCaller,
      });
      socket = null;
      if (!closedByCaller) scheduleReconnect();
    };
  };

  const scheduleReconnect = () => {
    if (closedByCaller || reconnectTimer) return;
    // 1s, 2s, 4s, 8s, 16s, 30s cap
    const delay = Math.min(1000 * 2 ** Math.max(0, attempt - 1), 30000);
    console.log("[realtime] reconnect_scheduled", { delay });
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      open();
    }, delay);
  };

  open();

  return {
    close: () => {
      closedByCaller = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (socket) {
        try { socket.close(); } catch { /* ignore */ }
        socket = null;
      }
    },
  };
}
